import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileAudio, Loader2, RefreshCw, Scissors, Trash2, Upload } from "lucide-react";
import { fmtClock, removeSourceAudio, sourceAudioUrl, uploadSourceAudio } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";
import SourceTempo from "@/components/creator/music/SourceTempo";

const HINTS = {
  add_band: "Upload just your singing (a cappella or a vocal-only file). ELPM keeps your voice and plays a full band under it.",
  add_singing: "Upload a beat or instrumental. ELPM sings the lyrics you write below over it.",
  grow_loop: "Upload your loop (a few bars of music, cut cleanly). It opens the song, and ELPM grows it into a full arrangement in the same tempo and key. Pick Instrumental below if you want to sing over it yourself, then press Sing it myself on the finished song.",
};

/** Upload the whole song (WAV, MP3, M4A, FLAC…) — big files go up in pieces; only the first 8 minutes are used. */
export default function SourceAudioUpload({ mode, projectId, ensureProject, source, onSource, maxSec }) {
  const fileRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const busy = progress !== null;

  const pick = async (file) => {
    setProgress(0);
    try {
      const id = projectId || (await ensureProject());
      const out = await uploadSourceAudio(id, file, setProgress);
      onSource(out);
      toast.success(mode === "grow_loop" ? "Loop uploaded" : "Song uploaded", { description: out.trimmed ? `Using the first ${fmtClock(maxSec)} of ${fmtClock(out.original_duration)}.` : `${fmtClock(out.duration)} ready to build on.` });
    } catch (e) {
      toast.error("Upload didn't finish", { description: safeApiError(e, "Please try again.") });
    } finally {
      setProgress(null);
    }
  };

  const remove = async () => {
    try { await removeSourceAudio(projectId); onSource(null); } catch (e) { toast.error("Couldn't remove it", { description: safeApiError(e, "Please try again.") }); }
  };

  const input = (
    <input ref={fileRef} data-testid="source-audio-input" type="file" accept="audio/*,.wav,.mp3,.m4a,.aac,.flac,.aif,.aiff,.ogg" className="hidden"
      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) pick(f); }} />
  );

  return (
    <div data-testid="source-audio-upload" className="mt-4 rounded-3xl border border-white/[0.08] bg-black/25 p-4">
      <p className="text-[12px] leading-relaxed text-white/60">{HINTS[mode]}</p>
      {input}
      {busy ? (
        <div data-testid="source-audio-progress" className="mt-4">
          <p className="flex items-center gap-2 text-sm text-blue-100"><Loader2 className="h-4 w-4 animate-spin" /> {progress < 1 ? `Uploading… ${Math.round(progress * 100)}%` : "Getting it ready…"}</p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-violet-500 transition-[width] duration-300" style={{ width: `${Math.max(4, progress * 100)}%` }} /></div>
        </div>
      ) : source?.status === "ready" ? (
        <div data-testid="source-audio-ready" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex min-w-0 items-center gap-2 text-sm text-white"><FileAudio className="h-4 w-4 shrink-0 text-emerald-300" /><span data-testid="source-audio-name" className="truncate">{source.filename}</span><span className="font-mono text-[11px] text-white/50">{fmtClock(source.duration)}</span></p>
            <div className="flex gap-2">
              <button type="button" data-testid="source-audio-replace" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[12px] text-white/70 transition-colors hover:bg-white/[0.08] hover:text-white"><RefreshCw className="h-3.5 w-3.5" /> Replace</button>
              <button type="button" data-testid="source-audio-remove" onClick={remove} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[12px] text-white/70 transition-colors hover:bg-rose-500/15 hover:text-rose-100"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
            </div>
          </div>
          {source.trimmed ? <p data-testid="source-audio-trimmed" className="flex items-center gap-1.5 text-[12px] text-amber-200/90"><Scissors className="h-3.5 w-3.5" /> Your song is {fmtClock(source.original_duration)} — ELPM uses the first {fmtClock(maxSec)}.</p> : null}
          <SourceTempo projectId={projectId} source={source} onSource={onSource} />
          <audio data-testid="source-audio-player" controls preload="metadata" src={sourceAudioUrl(projectId, source)} className="w-full" />
        </div>
      ) : (
        <button type="button" data-testid="source-audio-pick" onClick={() => fileRef.current?.click()}
          className="mt-4 flex w-full flex-col items-center gap-2 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-7 text-center transition-colors hover:border-blue-300/50 hover:bg-blue-500/[0.06]">
          <Upload className="h-5 w-5 text-blue-200" />
          <span className="font-display text-sm font-semibold text-white">{mode === "grow_loop" ? "Choose your loop file" : "Choose your song file"}</span>
          <span className="text-[11px] text-white/45">{mode === "grow_loop" ? "WAV, MP3, M4A, FLAC, AIFF or OGG · a loop of 2 seconds or more" : `WAV, MP3, M4A, FLAC, AIFF or OGG · full songs welcome · up to ${fmtClock(maxSec)} is used`}</span>
        </button>
      )}
    </div>
  );
}
